import React, { useEffect, useState, useCallback } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Users, Search, Package, MessageSquare, DollarSign,
  BarChart3, Download, X, Eye, Sparkles, CreditCard, MapPin, Layers,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const PERIODS = [
  ["today", "اليوم"], ["7d", "آخر 7 أيام"], ["30d", "آخر 30 يوم"], ["this_month", "هذا الشهر"],
  ["prev_month", "الشهر السابق"], ["3m", "3 أشهر"], ["6m", "6 أشهر"], ["12m", "12 شهر"], ["custom", "فترة مخصصة"],
];
const SMALL_TICK = { fontSize: 10, fill: "#666666" };

const Change = ({ pct }) => {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-extrabold ${up ? "text-green-600" : "text-red-500"}`} dir="ltr">
      {up ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{up ? "+" : ""}{pct}%
    </span>
  );
};

const Kpi = ({ icon: Icon, label, value, sub, change, onClick, testid }) => (
  <div onClick={onClick} data-testid={testid}
    className={`bg-white border border-[#7AB2B2] rounded-xl p-4 ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}>
    <div className="flex items-center justify-between">
      <div className="w-10 h-10 rounded-lg bg-[#EBF4F6] flex items-center justify-center text-[#09637E]"><Icon size={20} /></div>
      <Change pct={change} />
    </div>
    <div className="text-2xl font-extrabold text-[#09637E] mt-2" dir="ltr">{value}</div>
    <div className="text-sm font-bold text-[#333333]">{label}</div>
    {sub && <div className="text-xs text-[#666666] mt-0.5">{sub}</div>}
  </div>
);

const Section = ({ title, icon: Icon, children, testid }) => (
  <section className="bg-white border border-[#7AB2B2] rounded-2xl p-5" data-testid={testid}>
    <h2 className="text-lg font-extrabold text-[#333333] mb-3 flex items-center gap-2">
      <Icon className="text-[#09637E]" size={20} /> {title}
    </h2>
    {children}
  </section>
);

const MiniList = ({ items, labelKey, valueKey, empty }) => (
  !items?.length ? <div className="text-sm text-[#666666] py-3 text-center">{empty || "لا توجد بيانات كافية حالياً"}</div> :
  <div className="divide-y divide-[#EBF4F6]">
    {items.map((it, i) => (
      <div key={`${it[labelKey]}-${i}`} className="flex items-center justify-between py-2 text-sm">
        <span className="font-bold text-[#333333]">{it[labelKey] || "—"}</span>
        <span className="font-extrabold text-[#09637E]" dir="ltr">{typeof it[valueKey] === "number" ? it[valueKey].toLocaleString() : it[valueKey]}</span>
      </div>
    ))}
  </div>
);

const DrillModal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} dir="rtl">
    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="drill-modal">
      <div className="sticky top-0 bg-white border-b border-[#EBF4F6] p-4 flex items-center justify-between">
        <h3 className="font-extrabold text-[#333333]">{title}</h3>
        <button onClick={onClose} data-testid="drill-close" className="w-9 h-9 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center"><X size={18} /></button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  </div>
);

export default function FounderDashboard() {
  const [period, setPeriod] = useState("30d");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // {type, title, payload}

  const params = useCallback(() => {
    const p = { period };
    if (period === "custom" && custom.start && custom.end) { p.start = custom.start; p.end = custom.end; }
    return p;
  }, [period, custom]);

  const load = useCallback(async () => {
    if (period === "custom" && (!custom.start || !custom.end)) return;
    setLoading(true);
    try { setData((await api.get("/founder/overview", { params: params() })).data); }
    catch (e) { toast.error(arabicError(e)); }
    finally { setLoading(false); }
  }, [period, custom, params]);
  useEffect(() => { load(); }, [load]);

  const openTraders = async () => {
    try {
      const r = await api.get("/founder/drill/active_traders", { params: params() });
      setDrill({ type: "traders", title: "التجار النشطون", payload: r.data.items });
    } catch (e) { toast.error(arabicError(e)); }
  };
  const openProduct = async (p) => {
    try {
      const r = await api.get(`/founder/product/${p.id}`, { params: params() });
      setDrill({ type: "product", title: p.display_name || `${p.category} ${p.type}`, payload: r.data });
    } catch (e) { toast.error(arabicError(e)); }
  };
  const exportCsv = async (dataset) => {
    try {
      const r = await api.get("/founder/export", { params: { ...params(), dataset }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `imdad_${dataset}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(arabicError(e)); }
  };

  const t = data?.traders, sub = data?.subscriptions, ret = data?.retention, se = data?.searches,
    inv = data?.inventory, cv = data?.conversations, gm = data?.gmv;

  return (
    <div className="space-y-5" data-testid="founder-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333] flex items-center gap-2">
            <BarChart3 className="text-[#09637E]" size={32} /> إحصائيات إمداد
          </h1>
          <p className="text-[#666666] mt-1">Business Intelligence — بيانات حقيقية فقط من نشاط المنصة</p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={async () => {
            try {
              const r = await api.get("/founder/weekly-report.xlsx", { responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a"); a.href = url; a.download = `imdad_weekly_report_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
              URL.revokeObjectURL(url);
              toast.success("تم تحميل التقرير الأسبوعي (Excel)");
            } catch (e) { toast.error(arabicError(e)); }
          }} data-testid="download-weekly-report"
            className="h-9 px-3 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white text-xs font-bold flex items-center gap-1">
            <Download size={13} /> تحميل التقرير الأسبوعي (Excel)
          </button>
          {["traders", "inventory", "searches", "transactions"].map((d) => (
            <button key={d} onClick={() => exportCsv(d)} data-testid={`export-${d}`}
              className="h-9 px-3 rounded-lg border border-[#7AB2B2] text-[#09637E] text-xs font-bold flex items-center gap-1 hover:bg-[#EBF4F6]">
              <Download size={13} /> {{ traders: "التجار", inventory: "المخزون", searches: "البحث", transactions: "العمليات" }[d]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-1.5 flex-wrap items-center" data-testid="period-filters">
        {PERIODS.map(([v, label]) => (
          <button key={v} onClick={() => setPeriod(v)} data-testid={`period-${v}`}
            className={`h-9 px-3.5 rounded-lg text-xs font-bold ${period === v ? "bg-[#09637E] text-white" : "bg-white text-[#09637E] border border-[#7AB2B2]"}`}>
            {label}
          </button>
        ))}
        {period === "custom" && (
          <span className="flex items-center gap-1.5">
            <input type="date" value={custom.start} onChange={(e) => setCustom((s) => ({ ...s, start: e.target.value }))}
              data-testid="custom-start" className="h-9 rounded-lg border border-[#7AB2B2] px-2 text-xs" />
            <input type="date" value={custom.end} onChange={(e) => setCustom((s) => ({ ...s, end: e.target.value }))}
              data-testid="custom-end" className="h-9 rounded-lg border border-[#7AB2B2] px-2 text-xs" />
          </span>
        )}
      </div>

      {loading || !data ? (
        <div className="bg-white border border-[#7AB2B2] rounded-xl p-10 text-center text-[#666666]">جارٍ تحميل الإحصائيات...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <Kpi icon={Users} label="تجار نشطون" value={t.active} change={t.active_change_pct}
              sub={`من أصل ${t.approved} معتمد`} onClick={openTraders} testid="kpi-active-traders" />
            <Kpi icon={Users} label="تجار جدد" value={t.new} change={t.new_change_pct} sub={`معاد تفعيلهم: ${t.reactivated}`} testid="kpi-new-traders" />
            <Kpi icon={Search} label="عمليات البحث" value={se.total} change={se.change_pct}
              sub={`AI: ${se.ai} · معلومات: ${se.traditional}`} testid="kpi-searches" />
            <Kpi icon={Package} label="مخزون نشط" value={inv.active} sub={`الإجمالي: ${inv.total} · راكد: ${inv.stale}`} testid="kpi-inventory" />
            <Kpi icon={DollarSign} label="GMV الفترة $" value={gm.period.toLocaleString()} change={gm.change_pct}
              sub={gm.avg_transaction_value != null ? `متوسط الصفقة: $${gm.avg_transaction_value}` : "لا توجد صفقات بعد"} testid="kpi-gmv" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Section title="الاشتراكات (MRR / ARR)" icon={CreditCard} testid="sec-subscriptions">
              <div className="text-sm font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">{sub.message}</div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                <div className="bg-[#EBF4F6] rounded-lg p-2.5 text-center">
                  <div className="text-xl font-extrabold text-[#09637E]">{sub.trial_active}</div>
                  <div className="text-xs text-[#666666] font-bold">تجربة فعّالة</div>
                </div>
                <div className="bg-[#EBF4F6] rounded-lg p-2.5 text-center">
                  <div className="text-xl font-extrabold text-[#09637E]">{sub.trial_expired}</div>
                  <div className="text-xs text-[#666666] font-bold">تجربة منتهية</div>
                </div>
              </div>
              <p className="text-xs text-[#666666] mt-2">MRR / ARR / Churned MRR ستُحسب تلقائياً عند تفعيل الاشتراكات المدفوعة</p>
            </Section>
            <Section title="الاحتفاظ والفقد (نشاط)" icon={Users} testid="sec-retention">
              {ret.rate_pct === null ? (
                <div className="text-sm text-[#666666] py-4 text-center">لا توجد بيانات كافية حالياً (تحتاج فترتين متتاليتين من النشاط)</div>
              ) : (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-[#EBF4F6] rounded-lg p-2.5 text-center">
                    <div className="text-xl font-extrabold text-green-600" dir="ltr">{ret.rate_pct}%</div>
                    <div className="text-xs text-[#666666] font-bold">Retention</div>
                  </div>
                  <div className="bg-[#EBF4F6] rounded-lg p-2.5 text-center">
                    <div className="text-xl font-extrabold text-red-500" dir="ltr">{ret.activity_churn_rate_pct}%</div>
                    <div className="text-xs text-[#666666] font-bold">Activity Churn</div>
                  </div>
                </div>
              )}
              <p className="text-xs text-[#666666] mt-2">{ret.definition}</p>
            </Section>
            <Section title="تواصل التجار" icon={MessageSquare} testid="sec-conversations">
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[["محادثات نشطة", cv.active], ["رسائل", cv.messages], ["تجار متواصلون", cv.communicating_traders], ["أدّت لصفقة", cv.led_to_deal]].map(([l, v]) => (
                  <div key={l} className="bg-[#EBF4F6] rounded-lg p-2.5 text-center">
                    <div className="text-xl font-extrabold text-[#09637E]">{v}</div>
                    <div className="text-xs text-[#666666] font-bold">{l}</div>
                  </div>
                ))}
              </div>
              <div className="mt-2"><Change pct={cv.messages_change_pct} /> <span className="text-xs text-[#666666]">الرسائل مقارنة بالفترة السابقة</span></div>
            </Section>
          </div>

          <Section title="عمليات البحث يومياً" icon={Search} testid="sec-search-chart">
            {se.per_day.length === 0 ? <div className="text-sm text-[#666666] py-4 text-center">لا توجد عمليات بحث مسجلة في هذه الفترة (بدأ التتبع الآن)</div> : (
              <div className="h-52 w-full min-h-[13rem]" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={se.per_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EBF4F6" />
                    <XAxis dataKey="day" tick={SMALL_TICK} />
                    <YAxis tick={SMALL_TICK} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#09637E" radius={[4, 4, 0, 0]} maxBarSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {se.ai_success_rate_pct !== null && (
              <div className="text-xs text-[#666666] mt-2 flex items-center gap-1"><Sparkles size={13} className="text-[#09637E]" />
                نسبة نجاح بحث AI (نتائج &gt; 0): <b className="text-[#09637E]" dir="ltr">{se.ai_success_rate_pct}%</b></div>
            )}
          </Section>

          <Section title="المنتجات الأكثر طلباً" icon={Eye} testid="sec-top-products">
            <p className="text-xs text-[#666666] mb-2">{data.demand_score_formula}</p>
            {!data.top_products.length ? <div className="text-sm text-[#666666] py-3 text-center">لا توجد بيانات كافية حالياً — سيمتلئ هذا القسم مع تفاعل التجار</div> :
              <div className="divide-y divide-[#EBF4F6]">
                {data.top_products.map((p, i) => (
                  <div key={p.id} onClick={() => openProduct(p)} data-testid={`top-product-${i}`}
                    className="flex items-center gap-3 py-2 cursor-pointer hover:bg-[#EBF4F6]/60 rounded px-1">
                    <span className="w-7 h-7 rounded-full bg-[#EBF4F6] text-[#09637E] font-extrabold flex items-center justify-center text-sm shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[#333333] text-sm truncate">{p.display_name || `${p.category} · ${p.type}`}</div>
                      <div className="text-xs text-[#666666]">{p.merchant_name} · {p.size}</div>
                    </div>
                    <div className="text-xs text-[#666666] shrink-0">مشاهدات: <b>{p.period_views}</b> · حجوزات: <b>{p.period_reservations}</b></div>
                    <div className="font-extrabold text-[#09637E] shrink-0" dir="ltr">{p.demand_score}</div>
                  </div>
                ))}
              </div>}
          </Section>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Section title="الطلب حسب المدينة" icon={MapPin} testid="sec-demand-city">
              <MiniList items={se.by_city} labelKey="city" valueKey="count" empty="لا توجد عمليات بحث بعد" />
            </Section>
            <Section title="تجار حسب المدينة" icon={MapPin} testid="sec-traders-city">
              <MiniList items={data.traders_by_city} labelKey="city" valueKey="count" />
            </Section>
            <Section title="مخزون حسب المدينة" icon={MapPin} testid="sec-inv-city">
              <MiniList items={inv.by_city} labelKey="city" valueKey="count" />
            </Section>
            <Section title="GMV حسب المدينة" icon={DollarSign} testid="sec-gmv-city">
              <MiniList items={gm.by_city?.map((g) => ({ city: g.city, v: `$${g.gmv}` }))} labelKey="city" valueKey="v" empty="لا توجد صفقات مكتملة بعد" />
            </Section>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Section title="النمو حسب القطاع" icon={Layers} testid="sec-sector">
              <MiniList items={data.by_sector} labelKey="sector" valueKey="traders" />
              <p className="text-xs text-[#666666] mt-2">القطاع = نوع المواد التي يتاجر بها (سيراميك، بلاط...)</p>
            </Section>
            <Section title="المخزون حسب الفئة" icon={Package} testid="sec-inv-sector">
              <MiniList items={inv.by_sector} labelKey="sector" valueKey="count" />
            </Section>
            <Section title="توصيات AI" icon={Sparkles} testid="sec-ai-reco">
              <div className="bg-[#EBF4F6] rounded-lg p-3 text-center mb-2">
                <div className="text-xl font-extrabold text-[#09637E]">{data.ai_recommendations.generated}</div>
                <div className="text-xs text-[#666666] font-bold">توصيات مولّدة (تنبيهات السوق) خلال الفترة</div>
              </div>
              <p className="text-xs text-[#666666]">{data.ai_recommendations.message}</p>
            </Section>
          </div>

          <Section title="أكثر كلمات البحث" icon={Search} testid="sec-keywords">
            <MiniList items={se.top_keywords} labelKey="keywords" valueKey="count" empty="لا توجد عمليات بحث بالمعلومات بعد" />
          </Section>

          <div className="text-xs text-[#666666] flex items-center gap-2 flex-wrap">
            <span>GMV: {gm.definition}</span> · <span>اليوم: ${gm.today}</span> · <span>هذا العام: ${gm.year}</span>
          </div>
        </>
      )}

      {drill && (
        <DrillModal title={drill.title} onClose={() => setDrill(null)}>
          {drill.type === "traders" && (
            <div className="divide-y divide-[#EBF4F6]">
              {drill.payload.map((u) => (
                <div key={u.id} className="py-2 text-sm flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-bold text-[#333333]">{u.store_name} <span className="text-xs text-[#666666]">({u.first_name} {u.last_name})</span></div>
                    <div className="text-xs text-[#666666]">{u.city} · {(u.material_types || []).join("، ")}</div>
                  </div>
                  <div className="text-xs text-[#666666] text-left" dir="ltr">
                    {u.last_activity && <div>آخر نشاط: {u.last_activity.slice(0, 16).replace("T", " ")}</div>}
                    <div className={u.trial_active ? "text-green-600 font-bold" : "text-red-500 font-bold"}>
                      {u.trial_active ? "تجربة فعّالة" : "تجربة منتهية"} حتى {u.trial_end?.slice(0, 10)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {drill.type === "product" && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-[#EBF4F6] rounded-lg p-2.5"><div className="text-lg font-extrabold text-[#09637E]">{drill.payload.period_views}</div><div className="text-xs text-[#666666]">مشاهدات الفترة</div></div>
                <div className="bg-[#EBF4F6] rounded-lg p-2.5"><div className="text-lg font-extrabold text-[#09637E]">{drill.payload.reservations.length}</div><div className="text-xs text-[#666666]">حجوزات الفترة</div></div>
                <div className="bg-[#EBF4F6] rounded-lg p-2.5"><div className="text-lg font-extrabold text-[#09637E]">{drill.payload.product.search_count || 0}</div><div className="text-xs text-[#666666]">ظهور بالبحث (كلي)</div></div>
              </div>
              {drill.payload.viewer_cities.length > 0 && (
                <div><div className="font-bold text-[#333333] mb-1">مدن المشاهدين:</div>
                  <MiniList items={drill.payload.viewer_cities} labelKey="city" valueKey="count" /></div>
              )}
              {drill.payload.reservations.length > 0 && (
                <div><div className="font-bold text-[#333333] mb-1">الحجوزات:</div>
                  {drill.payload.reservations.map((r) => (
                    <div key={r.id} className="flex justify-between py-1 text-xs border-b border-[#EBF4F6]">
                      <span>{r.requester_name} · {r.meters} م</span>
                      <span className="font-bold" dir="ltr">${r.total_usd} — {r.status}</span>
                    </div>
                  ))}</div>
              )}
            </div>
          )}
        </DrillModal>
      )}
    </div>
  );
}
